import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface VendorAccess {
  vendorIds: string[];
  isOwner: boolean;
  isManager: boolean;
  loading: boolean;
}

/**
 * Hook pour gérer l'accès aux données vendeur
 * Retourne la liste des vendor_id accessibles (propriétaire + magasins gérés)
 */
export const useVendorAccess = (): VendorAccess => {
  const { user } = useAuth();
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchVendorAccess = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const accessibleVendorIds: string[] = [];

      // Vérifier directement le rôle utilisateur dans user_roles
      const { data: userRole, error: roleError } = await supabase
        .from('user_roles')
        .select('system_role')
        .eq('user_id', user.id)
        .single();

      // Vérifier le statut vendeur directement
      const { data: vendorProfile, error: vendorError } = await supabase
        .from('profiles')
        .select('vendor_status')
        .eq('id', user.id)
        .single();

      if (!vendorError && vendorProfile?.vendor_status === 'approved') {
        accessibleVendorIds.push(user.id);
        setIsOwner(true);
        console.log('[useVendorAccess] User is approved vendor:', user.id);
      }

      // Récupérer les magasins qu'il gère
      const { data: managedStores, error: managerError } = await supabase
        .from('vendor_managers')
        .select('vendor_id')
        .eq('manager_id', user.id);

      if (!managerError && managedStores && managedStores.length > 0) {
        console.log('[useVendorAccess] User manages stores:', managedStores);
        managedStores.forEach(store => {
          if (!accessibleVendorIds.includes(store.vendor_id)) {
            accessibleVendorIds.push(store.vendor_id);
          }
        });
        setIsManager(true);
      }

      console.log('[useVendorAccess] Final accessible vendor IDs:', accessibleVendorIds);
      setVendorIds(accessibleVendorIds);
    } catch (error) {
      console.error('[useVendorAccess] Error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchVendorAccess();
  }, [fetchVendorAccess]);

  return {
    vendorIds,
    isOwner,
    isManager,
    loading,
  };
};
